import { Head, Link, usePage } from "@inertiajs/react"
import { ArrowLeft, Download, Mic } from "lucide-react"

import { Badge } from "@/components/base/badges/badges"
import { Button } from "@/components/base/buttons/button"
import { ConversationView } from "@/components/replay/ConversationView"
import { EventInspector } from "@/components/replay/EventInspector"
import { ReplayControls } from "@/components/replay/ReplayControls"
import {
  type ReplayConversationEvent,
  type ReplayMetadata,
  ReplayProvider,
} from "@/components/replay/ReplayProvider"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface ReplayPageProps {
  conversation_id: number
  replay_metadata: ReplayMetadata
  initial_events?: ReplayConversationEvent[]
  ws_url?: string
  admin_token?: string
  [key: string]: unknown
}

export default function AdminReplay() {
  const {
    conversation_id,
    replay_metadata,
    initial_events,
    ws_url,
    admin_token,
  } = usePage<ReplayPageProps>().props

  const handleExport = async (format: "json" | "csv" | "md") => {
    // Use fetch directly for downloads
    const url = `/api/admin/replay_sessions/conversations/${conversation_id}/export.${format}`
    const response = await fetch(url, {
      headers: {
        "X-Admin-Token": admin_token ?? "admin-replay-access",
      },
    })

    if (response.ok) {
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = downloadUrl
      a.download = `conversation_${conversation_id}_events.${format}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(downloadUrl)
    }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Admin", href: "/admin" },
    { title: "Replay", href: "/admin/replay" },
    {
      title: `Conversation #${conversation_id}`,
      href: `/admin/replay/${conversation_id}`,
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Replay #${conversation_id}`} />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <ReplayProvider
          conversationId={conversation_id}
          replayMetadata={replay_metadata}
          initialEvents={initial_events ?? []}
          wsUrl={ws_url}
        >
          <div className="replay-container mx-auto max-w-[1800px] px-6 py-8">
            {/* Header */}
            <header className="replay-header mb-8 rounded-xl bg-white p-6 shadow-sm dark:bg-gray-900">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <Link href="/admin/replay">
                    <Button color="tertiary" size="md">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                  </Link>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      Replay: Conversation #{conversation_id}
                    </h1>
                    <Badge type="pill-color" color="error" size="md">
                      Admin Mode
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Karaoke mode: launch a fresh script-driven /chat run (not a
                      replay of this conversation). Subtle icon-only affordance. */}
                  <Button
                    color="tertiary"
                    size="md"
                    aria-label="Launch Karaoke mode"
                    title="Karaoke mode — a live, script-driven run"
                    onClick={() =>
                      window.location.assign("/chat?script=demo_golden_path")
                    }
                  >
                    <Mic className="h-4 w-4" />
                  </Button>
                  <Button
                    color="tertiary"
                    size="md"
                    onClick={() => void handleExport("json")}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export JSON
                  </Button>
                  <Button
                    color="tertiary"
                    size="md"
                    onClick={() => void handleExport("csv")}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                  <Button
                    color="tertiary"
                    size="md"
                    onClick={() => void handleExport("md")}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export Script
                  </Button>
                </div>
              </div>
            </header>

            {/* Controls at Top */}
            <div className="replay-toolbar mb-8">
              <ReplayControls />
            </div>

            {/* Main Layout */}
            <div className="replay-layout">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                {/* Left: Conversation View (2 columns on lg) */}
                <div className="space-y-8 lg:col-span-2">
                  <ConversationView />
                </div>

                {/* Right: Inspector Panel (1 column on lg) */}
                <div className="space-y-8">
                  <EventInspector />
                </div>
              </div>
            </div>
          </div>
        </ReplayProvider>
      </div>
    </AppLayout>
  )
}
